import React, { useContext } from 'react';

import AffectedBonusPeriods from 'components/AffectedBonusPeriods';
import Alert from 'kingpin/Alert';
import Typography from 'kingpin/atoms/Typography';

import {
  BonusMetricLockContext,
  BonusMetricUsage,
} from './BonusMetricLockProvider';

// List markers take the list's font, so match it to the Body 12 items
const LIST_STYLE: React.CSSProperties = {
  margin: 0,
  paddingLeft: 20,
  fontSize: 12,
  lineHeight: '21px',
  color: 'var(--color-text-secondary)',
};

const describeUsage = (usage: BonusMetricUsage) => {
  if (usage.isDirect || usage.viaCompoundMetricNames.length === 0) {
    return usage.programName;
  }

  return `${usage.programName} (via ${usage.viaCompoundMetricNames.join(
    ', ',
  )})`;
};

const UsageList = ({ usages }: { usages: BonusMetricUsage[] }) => (
  <ul style={LIST_STYLE}>
    {usages.map((usage) => (
      <li key={usage.programId}>
        <Typography.Body type="Body 12">{describeUsage(usage)}</Typography.Body>
      </li>
    ))}
  </ul>
);

const LockedBody = ({ usages }: { usages: BonusMetricUsage[] }) => (
  <>
    <UsageList usages={usages} />
    <Typography.Body type="Body 12">
      This metric feeds driver bonus calculations, so only FleetOps staff can
      change it. Contact FleetOps support if it needs to change.
    </Typography.Body>
    <Typography.Body type="Body 12">
      You can still use <b>Save As</b> to create a separate copy.
    </Typography.Body>
  </>
);

const StaffBody = ({ usages }: { usages: BonusMetricUsage[] }) => (
  <>
    <UsageList usages={usages} />
    <AffectedBonusPeriods
      programs={usages.map((u) => ({ id: u.programId, name: u.programName }))}
    />
    <Typography.Body type="Body 12">
      To change it only from a specific date, create a new version instead:
    </Typography.Body>
    <ol style={LIST_STYLE}>
      <li>
        <Typography.Body type="Body 12">
          Use <b>Save As</b> to create the new version.
        </Typography.Body>
      </li>
      <li>
        <Typography.Body type="Body 12">
          Point the bonus program at the new version from the relevant date.
        </Typography.Body>
      </li>
      <li>
        <Typography.Body type="Body 12">
          Keep the old version: both must exist at the same time. Flag it as
          legacy (e.g. prefix its name with &quot;[Legacy]&quot;) or archive it
          once nothing references it.
        </Typography.Body>
      </li>
    </ol>
  </>
);

const BonusMetricAlert = () => {
  const { usages, isLocked, requiresStaffConfirmation } = useContext(
    BonusMetricLockContext,
  );

  if (!isLocked && !requiresStaffConfirmation) {
    return null;
  }

  return (
    <div style={{ marginTop: 24 }} data-testid="bonus-metric-alert">
      <Alert
        type={'Warning'}
        title={
          isLocked
            ? 'This metric is used in a bonus program'
            : 'This metric is used in a bonus program: edit with care'
        }
        body={
          isLocked ? (
            <LockedBody usages={usages} />
          ) : (
            <StaffBody usages={usages} />
          )
        }
      />
    </div>
  );
};

export default BonusMetricAlert;
